import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Actions, PrimaryButton } from 'components/layout/Auth/AuthForm/styles';

import {
  CloseButton,
  Empty,
  Header,
  HeaderTexts,
  List,
  Option,
  OptionText,
  OptionTexts,
  OptionTitle,
  Overlay,
  Panel,
  Radio,
  SearchBlock,
  SearchField,
  SearchLabel,
  Text,
  Title,
} from './styles';

export type AddressOption = {
  id: string;
  title: string;
  description: string;
};

export type AddressSelectModalProps = {
  open: boolean;
  addresses: AddressOption[];
  value: string;
  // eslint-disable-next-line react/require-default-props
  title?: string;
  // eslint-disable-next-line react/require-default-props
  text?: string;
  // eslint-disable-next-line react/require-default-props
  confirmLabel?: string;
  // eslint-disable-next-line react/require-default-props
  loading?: boolean;
  // eslint-disable-next-line react/require-default-props
  dismissible?: boolean;
  onChange: (id: string) => void;
  onConfirm: () => void;
  onClose: () => void;
};

const normalize = (valor: string): string =>
  valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const CloseIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 3L13 13M13 3L3 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const SearchIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle
      cx="10.5"
      cy="10.5"
      r="6.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M15.5 15.5L20 20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const AddressSelectModal = ({
  open,
  addresses,
  value,
  title,
  text = 'Encontramos os seguintes endereços vinculados a sua conta. Selecione qual você deseja acessar?',
  confirmLabel = 'Continuar',
  loading = false,
  dismissible = true,
  onChange,
  onConfirm,
  onClose,
}: AddressSelectModalProps): JSX.Element | null => {
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    const termo = normalize(busca.trim());

    if (!termo) return addresses;

    return addresses.filter(
      (address) =>
        normalize(address.title).includes(termo) ||
        normalize(address.description).includes(termo)
    );
  }, [addresses, busca]);

  if (!open) return null;

  const conteudo = (
    <Overlay role="presentation" onClick={dismissible ? onClose : undefined}>
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Selecione um endereço'}
        onClick={(event) => event.stopPropagation()}
      >
        <Header>
          <HeaderTexts>
            {title && <Title>{title}</Title>}
            <Text>{text}</Text>
          </HeaderTexts>
          {dismissible && (
            <CloseButton type="button" aria-label="Fechar" onClick={onClose}>
              <CloseIcon />
            </CloseButton>
          )}
        </Header>

        <SearchBlock>
          <SearchLabel htmlFor="busca-endereco">Busca</SearchLabel>
          <SearchField>
            <input
              id="busca-endereco"
              type="search"
              placeholder="Digite"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
            />
            <SearchIcon />
          </SearchField>
        </SearchBlock>

        <List>
          {filtrados.length === 0 && (
            <Empty>Nenhum endereço encontrado para a busca.</Empty>
          )}
          {filtrados.map((address) => (
            <Option
              key={address.id}
              type="button"
              role="radio"
              aria-checked={address.id === value}
              selected={address.id === value}
              onClick={() => onChange(address.id)}
            >
              <OptionTexts>
                <OptionTitle>{address.title}</OptionTitle>
                <OptionText>{address.description}</OptionText>
              </OptionTexts>
              <Radio selected={address.id === value} />
            </Option>
          ))}
        </List>

        <Actions>
          <PrimaryButton
            type="button"
            shape="pill"
            fullWidth
            disabled={!value || loading}
            onClick={onConfirm}
          >
            {loading ? 'Aguarde...' : confirmLabel}
          </PrimaryButton>
        </Actions>
      </Panel>
    </Overlay>
  );

  if (typeof document === 'undefined') return conteudo;

  return createPortal(conteudo, document.body);
};

export default AddressSelectModal;
